import { RouteObject } from 'react-router-dom';

import MainLayout from '@/shared/components/layout/MainLayout';
import ProtectedRoute from './ProtectedRoute';

import WelcomePage from '../pages/WelcomePage';
import AboutPage from '../pages/AboutPage';
import ProjectPage from '../pages/ProjectPage';
import BlogPage from '../pages/BlogPage';
import BlogEditorPage from '../pages/BlogEditorPage';
import BlogReader from '../pages/BlogReader';
import LoginPage from '../pages/LoginPage';
import RegisterPage from '../pages/RegisterPage';
import LogoutPage from '../pages/LogoutPage';
import ForgotPasswordPage from '../pages/ForgotPasswordPage';
import ResetPasswordPage from '../pages/ResetPasswordPage';
import DashboardPage from '../pages/DashboardPage';
import DraftsPage from '../pages/DraftsPage';
import DevDebugPage from '../pages/DevDebugPage';
import NotFoundPage from '../pages/NotFoundPage';

import { UserRole } from '@/shared/types';
import { ROUTES } from '@/shared/services/constants';
import { ADMIN_ROUTES } from '@/shared';
import { PAGE_KEYS } from '@/shared/config/pageKeys';
import CalculatorPage from '@/pages/CalculatorPage';
import ToastmastersSuite from '@/pages/ToastmastersSuite';
import AdminDashboardPage from '@/pages/AdminDashboardPage';
import AdminUserManagementPage from '@/pages/AdminUserManagementPage';
import AdminBlogManagementPage from '@/pages/AdminBlogManagementPage';
import AdminAnalyticsPage from '@/pages/AdminAnalyticsPage';
import AdminPortfolioPage from '@/pages/AdminPortfolioPage';
import AdminFlashcardsPage from '@/pages/AdminFlashcardsPage';
import AdminEntitlementsPage from '@/pages/AdminEntitlementsPage';

/**
 * Application routes configuration
 * Defines the route structure including layout wrappers and protected routes
 */
const routes: RouteObject[] = [
  {
    path: '/',
    element: <MainLayout />,
    children: [
      // Public routes
      {
        index: true,
        element: <WelcomePage />,
      },
      {
        path: ROUTES.ABOUT,
        element: <AboutPage />,
      },
      {
        path: ROUTES.PROJECTS,
        element: <ProjectPage />,
      },
      {
        path: `${ROUTES.PROJECTS}/:id`,
        element: <ProjectPage />,
      },
      {
        path: ROUTES.BLOG,
        element: <BlogPage />,
      },
      {
        path: '/blog/preview/:id',
        element: (
          <ProtectedRoute requiredPageKey={PAGE_KEYS.BLOG_PREVIEW}>
            <BlogReader />
          </ProtectedRoute>
        ),
      },
      {
        path: `${ROUTES.BLOG}/:id`,
        element: <BlogReader />,
      },
      {
        path: ROUTES.LOGIN,
        element: <LoginPage />,
      },
      {
        path: ROUTES.REGISTER,
        element: <RegisterPage />,
      },
      {
        path: ROUTES.LOGOUT,
        element: <LogoutPage />,
      },
      {
        path: ROUTES.FORGOT_PASSWORD,
        element: <ForgotPasswordPage />,
      },
      {
        path: ROUTES.RESET_PASSWORD,
        element: <ResetPasswordPage />,
      },

      // Blog editor routes - require authentication
      {
        path: ROUTES.BLOG_CREATE,
        element: (
          <ProtectedRoute requiredPageKey={PAGE_KEYS.BLOG_CREATE}>
            <BlogEditorPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/blog/edit/:id',
        element: (
          <ProtectedRoute requiredPageKey={PAGE_KEYS.BLOG_EDIT}>
            <BlogEditorPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.TOASTMASTERS_SUITE,
        element: (
          <ProtectedRoute requiredPageKey={PAGE_KEYS.TOASTMASTERS}>
            <ToastmastersSuite />
          </ProtectedRoute>
        ),
      },

      // Protected routes - require authentication
      {
        path: ROUTES.DASHBOARD,
        element: (
          <ProtectedRoute requiredPageKey={PAGE_KEYS.DASHBOARD}>
            <DashboardPage />
          </ProtectedRoute>
        ),
      },
      {
        path: `${ROUTES.DASHBOARD}/:section`,
        element: (
          <ProtectedRoute requiredPageKey={PAGE_KEYS.DASHBOARD}>
            <DashboardPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.DRAFTS,
        element: (
          <ProtectedRoute requiredPageKey={PAGE_KEYS.DRAFTS}>
            <DraftsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.PROFILE,
        element: (
          <ProtectedRoute requiredPageKey={PAGE_KEYS.PROFILE}>
            <DashboardPage section="profile" />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.SETTINGS,
        element: (
          <ProtectedRoute requiredPageKey={PAGE_KEYS.SETTINGS}>
            <DashboardPage section="settings" />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.CALCULATOR,
        element: (
          <ProtectedRoute requiredPageKey={PAGE_KEYS.CALCULATOR}>
            <CalculatorPage />
          </ProtectedRoute>
        ),
      },

      // Admin routes - require admin role
      {
        path: ADMIN_ROUTES.DASHBOARD,
        element: (
          <ProtectedRoute requiredRoles={[UserRole.ADMIN]} requiredPageKey={PAGE_KEYS.ADMIN_DASHBOARD}>
            <AdminDashboardPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_ROUTES.USERS,
        element: (
          <ProtectedRoute requiredRoles={[UserRole.ADMIN]} requiredPageKey={PAGE_KEYS.ADMIN_USERS}>
            <AdminUserManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_ROUTES.BLOGS,
        element: (
          <ProtectedRoute requiredRoles={[UserRole.ADMIN]} requiredPageKey={PAGE_KEYS.ADMIN_BLOGS}>
            <AdminBlogManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_ROUTES.ANALYTICS,
        element: (
          <ProtectedRoute requiredRoles={[UserRole.ADMIN]} requiredPageKey={PAGE_KEYS.ADMIN_ANALYTICS}>
            <AdminAnalyticsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_ROUTES.PORTFOLIO,
        element: (
          <ProtectedRoute requiredRoles={[UserRole.ADMIN]} requiredPageKey={PAGE_KEYS.ADMIN_PORTFOLIO}>
            <AdminPortfolioPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_ROUTES.FLASHCARDS,
        element: (
          <ProtectedRoute requiredRoles={[UserRole.ADMIN]} requiredPageKey={PAGE_KEYS.ADMIN_FLASHCARDS}>
            <AdminFlashcardsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ADMIN_ROUTES.ENTITLEMENTS,
        element: (
          <ProtectedRoute requiredRoles={[UserRole.ADMIN]} requiredPageKey={PAGE_KEYS.ADMIN_ENTITLEMENTS}>
            <AdminEntitlementsPage />
          </ProtectedRoute>
        ),
      },

      // Development debug route - only available in development, admin-gated
      {
        path: '/dev/debug',
        element: (
          <ProtectedRoute requiredRoles={[UserRole.ADMIN]}>
            <DevDebugPage />
          </ProtectedRoute>
        ),
      },

      // Catch-all route - render 404 page
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
];

export default routes;